import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Crown, Zap, ArrowLeft, Loader2, Sparkles } from "lucide-react";

const FREE = ["15 ECO mentor messages / day", "Beginner & Intermediate lessons", "Business Dictionary & Search", "Patois Translator", "Startup idea analysis", "Business IQ Test"];
const PRO = ["Unlimited ECO mentor", "All Advanced & Expert lessons", "Negotiation Simulator", "Deal Builder", "Business Plan & GTM generators", "Advanced learning paths", "Priority everything"];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const isPro = user?.is_pro || user?.tier === "pro";

  const checkout = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: billing === "yearly" ? "pro_yearly" : "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setLoading(false); }
  };

  const price = billing === "yearly" ? 290 : 29;
  const period = billing === "yearly" ? "/year" : "/month";

  return (
    <div className="min-h-screen bg-[#050505] glow-grid text-slate-200">
      <header className="glass border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Back</Link>
          <div className="font-display font-bold text-white">ECO</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-16 text-center">
        <span className="eco-tag inline-flex items-center gap-2 font-mono-eco uppercase tracking-widest mb-5"><Zap size={13} className="text-[#4d8bff]" /> Pricing</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">Invest in your business brain.</h1>
        <p className="text-slate-400 mt-4 max-w-xl mx-auto">Start free. Upgrade when you're ready for unlimited mentoring and the full toolkit.</p>

        <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1 mt-8">
          <button data-testid="billing-monthly" onClick={() => setBilling("monthly")} className={`text-sm font-medium px-4 py-2 rounded-full ${billing === "monthly" ? "bg-white text-black" : "text-slate-400"}`}>Monthly</button>
          <button data-testid="billing-yearly" onClick={() => setBilling("yearly")} className={`text-sm font-medium px-4 py-2 rounded-full ${billing === "yearly" ? "bg-white text-black" : "text-slate-400"}`}>Yearly <span className="text-emerald-400">·save 17%</span></button>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-12 text-left max-w-3xl mx-auto">
          {/* Free */}
          <div className="eco-card p-8">
            <h3 className="font-display text-xl font-semibold text-white">Free</h3>
            <div className="font-display text-4xl font-bold text-white mt-3">$0</div>
            <p className="text-sm text-slate-500 mt-1">Forever</p>
            <ul className="space-y-3 mt-6">
              {FREE.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300"><Check size={16} className="text-slate-500 mt-0.5 shrink-0" /> {f}</li>)}
            </ul>
            <Link to={user ? "/dashboard" : "/login"} className="btn-secondary w-full mt-8">{user ? "Current plan" : "Get started"}</Link>
          </div>

          {/* Pro */}
          <div className="eco-card p-8 border-[#0055FF]/40 relative" style={{ boxShadow: "0 0 40px rgba(0,85,255,0.12)" }}>
            <span className="absolute -top-3 left-8 eco-tag bg-[#0055FF] text-white border-0 flex items-center gap-1"><Crown size={12} /> Most popular</span>
            <h3 className="font-display text-xl font-semibold text-white">ECO Pro</h3>
            <div className="font-display text-4xl font-bold text-white mt-3">${price}<span className="text-lg text-slate-500">{period}</span></div>
            <p className="text-sm text-emerald-400 mt-1.5 font-medium flex items-center gap-1.5" data-testid="trial-badge"><Sparkles size={14} /> 3-day free trial · no card required</p>
            <p className="text-sm text-slate-500 mt-2">Everything in Free, plus:</p>
            <ul className="space-y-3 mt-6">
              {PRO.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200"><Check size={16} className="text-[#4d8bff] mt-0.5 shrink-0" /> {f}</li>)}
            </ul>
            {isPro ? (
              <button disabled className="btn-secondary w-full mt-8">You're on Pro 🎉</button>
            ) : (
              <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="btn-accent w-full mt-8">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <><Zap size={16} /> Start 3-day free trial</>}
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-600 mt-8">Secure checkout via Stripe. No card required for your trial — cancel anytime.</p>
        <div className="mt-4 flex items-center justify-center gap-5 text-xs">
          <Link to="/terms" className="text-slate-600 hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/privacy" className="text-slate-600 hover:text-white transition-colors">Privacy Policy</Link>
          <a href="mailto:ecoentrepreneuros@gmail.com" className="text-slate-600 hover:text-white transition-colors">Contact</a>
        </div>
      </div>
    </div>
  );
}
